import React, { useState, useEffect } from 'react';
import { AlertTriangle, Zap, Eye, Check, Calendar, TrendingUp, TrendingDown, Target, Mail, Play, Search, CheckCircle, Radio } from 'lucide-react';
import { SALES_BRIEFING } from '../../data/salesBriefing';
import { DAVID_EMAIL_REPLIES } from '../../data/davidEmailReplyData';
import DAVID_DEAL_SCENARIOS from '../../data/davidDealAdHocData';
import { PA1_EXECUTION, PA1_SHARE_DETAILS, PA1_REQUEST_CHANGE_RESPONSES, PA1_CHANGE_MESSAGES } from '../../data/executionData';
import { AGENT_THUMBS, SectionLabel, MeetingItem, ExpandedDetails, UrgencyBadge, InboxItem, NeedsDecisionCard, RippledTaskCard, RippledInboxItem, RippledApprovalCard } from './V4BriefingShared';
import V4EmailReply from './V4EmailReply';
import V4TaskExecution from './V4TaskExecution';
import V4RiskBriefing from './V4RiskBriefing';
import V4ExecutionView from './V4ExecutionView';
import V4AdHocChat from './V4AdHocChat';

const DEAL_STATUS_STYLES = {
  'at-risk': { label: 'At Risk', dark: 'text-red-400 bg-red-400/10', light: 'text-red-600 bg-red-600/10' },
  'closing': { label: 'Closing', dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10' },
  'active': { label: 'Active', dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10' },
  'stalled': { label: 'Stalled', dark: 'text-white/40 bg-white/[0.06]', light: 'text-slate-500 bg-slate-200' },
  'engaged': { label: 'Engaged', dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10' },
  'won': { label: 'Closed Won', dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10' },
};

const DealCard = ({ deal, isDark, onDeepDive, overrideState }) => {
  const [expanded, setExpanded] = useState(false);
  const effectiveStatus = overrideState || deal.status;
  const isHandled = !!overrideState;
  const hasAlert = deal.alert && !isHandled;
  const cardBg = isHandled
    ? (isDark ? 'bg-indigo-950/15' : 'bg-indigo-50/50')
    : hasAlert
      ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60')
      : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isHandled
    ? (isDark ? 'border-indigo-500/15' : 'border-indigo-200/60')
    : hasAlert
      ? (isDark ? 'border-red-500/15' : 'border-red-200/60')
      : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const status = DEAL_STATUS_STYLES[effectiveStatus];
  const agentThumb = AGENT_THUMBS[deal.agent];

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`deal-card-${deal.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{deal.title}</p>
        {status && (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? status.dark : status.light}`}>
            {effectiveStatus === 'closing' && <Radio className="w-2.5 h-2.5" />}
            {status.label}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-1 ml-7">
        <span className={`text-[11px] font-medium ${textPrimary}`}>{deal.value}</span>
        <span className={`text-[10px] ${textSecondary}`}>{deal.stage}</span>
        {deal.daysInStage && <span className={`text-[10px] ${textSecondary}`}>{deal.daysInStage}d in stage</span>}
      </div>
      <p className={`text-[11px] mt-1 ml-7 ${textSecondary}`}>{deal.description}</p>

      <div className="flex items-center gap-1.5 mt-2 ml-7">
        {!isHandled && (
          <button
            onClick={() => onDeepDive?.(deal.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'}`}
            data-testid={`deal-deep-dive-${deal.id}`}
          >
            <Search className="w-2.5 h-2.5" /> Deep Dive
          </button>
        )}
        {deal.expanded && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`}
            data-testid={`deal-more-${deal.id}`}
          >
            <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
          </button>
        )}
      </div>
      {expanded && deal.expanded && <ExpandedDetails item={deal} isDark={isDark} />}
    </div>
  );
};

const MetricSnapshotRow = ({ metric, isDark }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const isPositive = metric.trend?.startsWith('+');
  const isNegative = metric.trend?.startsWith('-');
  const trendColor = metric.alert
    ? (isDark ? 'text-red-400' : 'text-red-500')
    : isPositive
      ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
      : isNegative
        ? (isDark ? 'text-red-400' : 'text-red-500')
        : textSecondary;

  return (
    <div className="flex items-center justify-between px-2.5 py-2">
      <span className={`text-xs font-semibold ${textPrimary}`}>{metric.label}</span>
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-medium ${textPrimary}`}>{metric.value}</span>
        {metric.trend && (
          <span className={`flex items-center gap-0.5 text-[11px] ${trendColor}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : isNegative ? <TrendingDown className="w-3 h-3" /> : null}
            {metric.trend}
          </span>
        )}
      </div>
    </div>
  );
};

const TaskCard = ({ item, isDark, onBeginTask, isCompleted }) => {
  const [expanded, setExpanded] = useState(false);
  const cardBg = isCompleted ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isCompleted ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const agentThumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`task-card-${item.id}`}>
      <div className="flex items-center gap-2">
        {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${textPrimary}`}>{item.title}</p>
        {isCompleted ? (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}><Check className="w-2.5 h-2.5" /> Completed</span>
        ) : item.urgency && <UrgencyBadge urgency={item.urgency} isDark={isDark} />}
      </div>
      <p className={`text-[11px] mt-1 line-clamp-1 ml-7 ${textSecondary}`}>{item.description}</p>
      {!isCompleted && (
        <div className="flex items-center gap-1.5 mt-2 ml-7">
          <button onClick={() => onBeginTask?.(item.id)} className="flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-500/20 text-indigo-400 text-[10px] font-medium hover:bg-indigo-500/30 transition-colors" data-testid={`begin-task-${item.id}`}>
            <Play className="w-2.5 h-2.5" /> Begin
          </button>
          {item.expanded && (
            <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${textSecondary} ${hoverBg}`} data-testid={`more-task-${item.id}`}>
              <Eye className="w-2.5 h-2.5" /> {expanded ? 'Less' : 'More'}
            </button>
          )}
        </div>
      )}
      {expanded && item.expanded && <ExpandedDetails item={item} isDark={isDark} />}
    </div>
  );
};

const PipelineAlertCard = ({ alert, isDark, onResolve, isResolved }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const cardBg = isResolved ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50') : (isDark ? 'bg-red-950/20' : 'bg-red-50/60');
  const border = isResolved ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : (isDark ? 'border-red-500/15' : 'border-red-200/60');

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`pipeline-alert-${alert.id}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isResolved ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs font-semibold flex-1 ${textPrimary}`}>{alert.title}</p>
            {isResolved && (
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
                <CheckCircle className="w-2.5 h-2.5" /> Resolved
              </span>
            )}
          </div>
          <p className={`text-[11px] mt-1 leading-relaxed ${textSecondary}`}>{alert.description}</p>
          {!isResolved && (
            <div className="flex items-center gap-1.5 mt-2">
              <button onClick={() => onResolve?.()} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'}`} data-testid={`resolve-alert-${alert.id}`}>
                <Search className="w-2.5 h-2.5" /> Resolve
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const V4SalesBriefing = ({ isDark, onFullscreenChange, davidState, ripples, onRippleComplete, onRippleAck }) => {
  const data = SALES_BRIEFING;
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const [activeEmail, setActiveEmail] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [activeAlert, setActiveAlert] = useState(null);
  const [alertPhase, setAlertPhase] = useState('briefing');
  const [activeDeal, setActiveDeal] = useState(null);

  const isFullscreen = !!(activeEmail || activeTask || activeAlert || activeDeal);
  useEffect(() => { onFullscreenChange?.(isFullscreen); }, [isFullscreen, onFullscreenChange]);

  const handledEmails = davidState?.handledEmails || {};
  const completedTasks = davidState?.completedTasks || new Set();
  const resolvedAlerts = davidState?.resolvedAlerts || new Set();
  const dealStates = davidState?.dealStates || {};
  const decisions = davidState?.decisions || {};

  const pendingEmails = data.inbox.filter((e) => !handledEmails[e.id]);
  // Merge today + if-time into one list, renders uniformly with the same depth
  const allTasks = [...(data.tasks.today || []), ...(data.tasks.ifTime || [])];

  // Handlers
  const handleEmailReply = (id) => setActiveEmail(id);
  const handleEmailAction = (id, action) => davidState?.markDavidEmailDone?.(id, action);
  const handleEmailBack = (type, nextEmailId) => {
    if (type === 'next' && nextEmailId) setActiveEmail(nextEmailId);
    else setActiveEmail(null);
  };

  const handleBeginTask = (id) => setActiveTask(id);
  const handleTaskComplete = () => { if (activeTask) davidState?.completeDavidTask?.(activeTask); };
  const handleBackFromTask = () => setActiveTask(null);

  const handleResolveAlert = () => { setActiveAlert('pa1'); setAlertPhase('briefing'); };
  const handleExecuteAlert = () => setAlertPhase('executing');
  const handleBackFromAlert = () => {
    if (alertPhase === 'executing' && activeAlert) davidState?.resolveDavidAlert?.(activeAlert);
    setActiveAlert(null);
    setAlertPhase('briefing');
  };

  const handleDealDeepDive = (id) => setActiveDeal(id);
  const handleBackFromDeal = () => {
    if (activeDeal) davidState?.setDavidDealState?.(activeDeal, 'engaged');
    setActiveDeal(null);
  };

  const handleDecisionApprove = (id) => davidState?.setDavidDecision?.(id, 'approved');
  const handleDecisionReject = (id) => davidState?.setDavidDecision?.(id, 'rejected');

  // Fullscreen views
  if (activeEmail) {
    const emailData = data.inbox.find((e) => e.id === activeEmail);
    return (
      <V4EmailReply
        isDark={isDark}
        emailId={activeEmail}
        emailData={emailData}
        onBack={handleEmailBack}
        onEmailHandled={handleEmailAction}
        pendingEmails={pendingEmails}
        allReplies={DAVID_EMAIL_REPLIES}
      />
    );
  }

  if (activeAlert) {
    if (alertPhase === 'executing') {
      return (
        <V4ExecutionView
          isDark={isDark}
          onBack={handleBackFromAlert}
          executionData={PA1_EXECUTION}
          shareDetailsData={PA1_SHARE_DETAILS}
          requestChangeData={PA1_REQUEST_CHANGE_RESPONSES}
          requestChangeMessagesData={PA1_CHANGE_MESSAGES}
          backLabel="Back to Briefing"
        />
      );
    }
    return (
      <V4RiskBriefing
        isDark={isDark}
        riskId={activeAlert}
        onBack={handleBackFromAlert}
        onExecute={handleExecuteAlert}
      />
    );
  }

  if (activeTask) {
    const taskData = allTasks.find((t) => t.id === activeTask);
    return (
      <V4TaskExecution
        isDark={isDark}
        taskId={activeTask}
        onBack={handleBackFromTask}
        onComplete={handleTaskComplete}
        simState={null}
        taskData={taskData}
      />
    );
  }

  if (activeDeal) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={activeDeal}
        scenarios={DAVID_DEAL_SCENARIOS}
        backLabel="Back to Sales"
        onBack={handleBackFromDeal}
      />
    );
  }

  // Default briefing view (4-column: Inbox+Meetings | Tasks+Alert | Deals+Metrics | Needs Decision)
  const pendingTaskCount = Math.max(0, allTasks.length - completedTasks.size);
  return (
    <div className="flex flex-col h-full px-5 py-4" data-testid="sales-briefing">
      <div className="mb-4 flex-shrink-0">
        <div className="flex gap-3">
          <img src={AGENT_THUMBS.luc} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-[15px] ${isDark ? 'text-white/70' : 'text-slate-700'} leading-relaxed`}>
              {data.greeting.lucMessage}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`text-[11px] flex items-center gap-1 ${textSecondary}`}>
                <Calendar className="w-3 h-3" /> {data.greeting.meetings} meetings
              </span>
              <span className={`text-[11px] flex items-center gap-1 ${textSecondary}`}>
                <Check className="w-3 h-3" /> {pendingTaskCount} tasks
              </span>
              {!resolvedAlerts.has('pa1') && data.greeting.dealAlerts > 0 && (
                <span className={`text-[11px] flex items-center gap-1 ${isDark ? 'text-red-400' : 'text-red-500'}`}>
                  <AlertTriangle className="w-3 h-3" /> {data.greeting.dealAlerts} deals at risk
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 flex-1 min-h-0">
        {/* Col 1: Inbox + Meetings */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          {(pendingEmails.length > 0 || (ripples?.emails?.length || 0) > 0) && (
            <>
              <SectionLabel icon={Mail} title="Inbox Snapshot" count={pendingEmails.length + (ripples?.emails?.length || 0)} isDark={isDark} />
              <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto pr-0.5">
                {pendingEmails.map((email) => (
                  <InboxItem key={email.id} email={email} isDark={isDark} onReply={handleEmailReply} />
                ))}
                {(ripples?.emails || []).map((email) => (
                  <RippledInboxItem key={email.id} email={email} isDark={isDark} onHandle={onRippleAck ? (id) => onRippleAck(id, 'handled') : undefined} />
                ))}
              </div>
            </>
          )}
          <div className="pt-4">
            <SectionLabel icon={Calendar} title="Meetings" count={data.meetings.length} isDark={isDark} />
            <div className="flex flex-col gap-2">
              {data.meetings.map((m) => (
                <MeetingItem key={m.id} meeting={m} isDark={isDark} />
              ))}
            </div>
          </div>
        </div>

        {/* Col 2: Tasks + Pipeline Alert */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          <SectionLabel icon={Zap} title="Today's Tasks" count={pendingTaskCount + (ripples?.tasks?.length || 0)} isDark={isDark} accent="amber" />
          <div className="flex flex-col gap-3">
            {allTasks.map((item) => (
              <TaskCard key={item.id} item={item} isDark={isDark} onBeginTask={handleBeginTask} isCompleted={completedTasks.has(item.id)} />
            ))}
            {(ripples?.tasks || []).map((task) => (
              <RippledTaskCard key={task.id} task={task} isDark={isDark} onComplete={onRippleComplete} />
            ))}
          </div>
          {data.pipelineMetrics.alert && (
            <div className="pt-4">
              <SectionLabel icon={AlertTriangle} title="Pipeline Alert" isDark={isDark} accent="red" />
              <PipelineAlertCard alert={data.pipelineMetrics.alert} isDark={isDark} onResolve={handleResolveAlert} isResolved={resolvedAlerts.has('pa1')} />
            </div>
          )}
        </div>

        {/* Col 3: Active Deals + Pipeline Metrics */}
        <div className="flex flex-col min-h-0 pr-1">
          <SectionLabel icon={Target} title="Active Deals" count={data.deals.length} isDark={isDark} accent="amber" />
          <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-0.5">
            {data.deals.map((d) => (
              <DealCard key={d.id} deal={d} isDark={isDark} onDeepDive={handleDealDeepDive} overrideState={dealStates[d.id]} />
            ))}
          </div>
          <div className="pt-4">
            <SectionLabel icon={TrendingUp} title="Pipeline Metrics" isDark={isDark} accent="emerald" />
            <div className={`rounded-lg border ${border} overflow-hidden ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
              {data.pipelineMetrics.snapshot.map((m, i) => (
                <div key={i} className={i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}>
                  <MetricSnapshotRow metric={m} isDark={isDark} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Col 4: Needs Decision */}
        <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
          <SectionLabel icon={CheckCircle} title="Needs Decision" count={(data.needsDecision || []).length + (ripples?.approvals?.length || 0)} isDark={isDark} accent="amber" />
          <div className="flex flex-col gap-3">
            {(data.needsDecision || []).map((item) => (
              <NeedsDecisionCard
                key={item.id}
                item={item}
                isDark={isDark}
                onApprove={handleDecisionApprove}
                onReject={handleDecisionReject}
                decisionState={decisions[item.id]}
              />
            ))}
            {(ripples?.approvals || []).map((item) => (
              <RippledApprovalCard key={item.id} item={item} isDark={isDark} onApprove={(id) => { onRippleAck?.(id, 'approved'); onRippleComplete?.(id); }} onReject={(id) => onRippleAck?.(id, 'rejected')} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4SalesBriefing;
